/**
 * «Settimana di spedizione» — the ship-week a lane-book line is released in under the units-sent cap
 * (bata-reqs ADR 9000 open question 1, bata-platform #891, migration `9041b7e2c5a3`).
 *
 * The platform carries `tranche_week` on every lane-book line (`ConsolidationLineSummary`) and on the
 * exit-lane execution pack (`ExitLanePackLine`): the Monday of the ISO week the line ships in. Forced
 * withdrawals and outlet moves from a sender with a cap in force are released in weekly tranches, so
 * one move can span several weeks. `null` means the line was not released under a cap — every
 * consolidation line, every older line, every forced line whose sender had no cap — and it ships as
 * before: the cell is EMPTY, never «n.d.», on screen and in the file (`DataColumn.blankWhenAbsent`).
 *
 * The lane book and the pack carry the week; neither refuses to approve or execute a future tranche
 * early (platform #891, ambiguity 8). The column says the week, it does not enforce it.
 */

import { useTranslation } from 'react-i18next';

import type { ExportCell, ExportColumn } from '@/export';
import { formatDate } from '@/lib/format';
import type { DataColumn } from '@/planning';

export const TRANCHE_WEEK_HEADER_KEY = 'exit.column.trancheWeek';

/** A line's tranche week, or `null`: shipped as before. */
export function trancheWeekOf(line: { readonly tranche_week?: string | null }): string | null {
  const week = line.tranche_week;
  return week === undefined || week === null || week === '' ? null : week;
}

/** The cell: the week's Monday as a date, or empty. */
export function TrancheWeekCell({ week }: { readonly week: string | null }): React.JSX.Element {
  const { i18n } = useTranslation();
  return <td className="font-numeric tabular-nums">{week === null ? null : formatDate(week, i18n.language, 'UTC')}</td>;
}

/** The column, for any table of lane-book lines. `descriptionKey` is the table's own (i). */
export function trancheWeekColumn<T extends { readonly tranche_week?: string | null }>(descriptionKey: string): DataColumn<T> {
  return {
    id: 'tranche_week', headerKey: TRANCHE_WEEK_HEADER_KEY, descriptionKey, kind: 'date', blankWhenAbsent: true,
    value: (line) => trancheWeekOf(line),
    cell: (line) => <TrancheWeekCell week={trancheWeekOf(line)} />,
  };
}

/** The file's cell: the ISO date, or an empty cell — not applicable, never «not reported». */
export function trancheWeekExportCell(week: string | null): ExportCell {
  return week === null ? { kind: 'text', value: '' } : { kind: 'date', iso: week };
}

/** The lane book's file column. */
export const TRANCHE_WEEK_EXPORT_COLUMN: ExportColumn = {
  key: 'tranche_week', headerKey: TRANCHE_WEEK_HEADER_KEY, descriptionKey: 'exit.columnGuide.lane.trancheWeek', kind: 'date',
};
